import React, { useState } from 'react';
import axios from 'axios';
import withRoleAccess from '../../hoc/withRoleAccess';
import Sidebar from '../../components/Sidebar/Sidebar';
import Header from '../../components/Header/Header';
import { useTranslation } from 'react-i18next';
import './InventoryPage.css';
import {
  Form,
  Input,
  InputNumber,
  Select,
  Button,
  Card,
  Typography,
  message,
  Layout,
  Space
} from 'antd';
import {
  ShoppingOutlined,
  SaveOutlined,
  ClearOutlined
} from '@ant-design/icons';

const { Content } = Layout;
const { Title } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const InventoryPage = () => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const API_URL = process.env.REACT_APP_API_URL;
  
const categories = [
  { value: 'Registration Fee', label: t('Registration') },
  { value: 'A Level CIE', label: t('A Level CIE') },
  { value: 'A Level Edexcel', label: t('A Level Edexcel') },
  { value: 'ACT', label: t('ACT') },
  { value: 'AP', label: t('AP') },
  { value: 'Cambridge English', label: t('Cambridge English') },
  { value: 'Cambridge Primary', label: t('Cambridge Primary') },
  { value: 'Cambridge Lower Secondary', label: t('Cambridge Lower Secondary') },
  { value: 'Checkpoint', label: t('Checkpoint') },
  { value: 'Checkpoint SPIP', label: t('Checkpoint SPIP') },
  { value: 'Chemistry', label: t('Chemistry') },
  { value: 'CU Exam', label: t('CU Exam') },
  { value: 'English', label: t('English') },
  { value: 'English Literacy', label: t('English Literacy') },
  { value: 'GED', label: t('GED') },
  { value: 'GMAT', label: t('GMAT') },
  { value: 'IB', label: t('IB') },
  { value: 'IELTS', label: t('IELTS') },
  { value: 'IGCSE AQA', label: t('IGCSE AQA') },
  { value: 'IGCSE CIE', label: t('IGCSE CIE') },
  { value: 'IGCSE Edexcel', label: t('IGCSE Edexcel') },
  { value: 'ISEB', label: t('ISEB') },
  { value: 'Key Stage', label: t('Key Stage') },
  { value: 'KMIDS', label: t('KMIDS') },
  { value: 'MAP Test', label: t('MAP Test') },
  { value: 'Math', label: t('Math') },
  { value: 'Math Olympiad', label: t('Math Olympiad') },
  { value: 'MYP', label: t('MYP') },
  { value: 'Physics', label: t('Physics') },
  { value: 'PSAT', label: t('PSAT') },
  { value: 'SAT', label: t('SAT') },
  { value: 'Science', label: t('Science') },
  { value: 'SSAT', label: t('SSAT') },
  { value: 'Summer English', label: t('Summer English') },
  { value: 'Summer Math', label: t('Summer Math') },
  { value: 'Test Fee', label: t('Test Fee') },
  { value: 'Thai', label: t('Thai') },
  { value: 'TOEFL', label: t('TOEFL') },
  { value: 'TOEIC', label: t('TOEIC') },
  { value: 'TU-GET', label: t('TU-GET') },
  { value: 'White Rose Maths', label: t('White Rose Maths') },
  { value: 'Writing', label: t('Writing') },
  { value: 'Original Book', label: t('Original Book') },
  { value: 'Others', label: t('Others') }
];


  const handleAddItem = async (values) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/inventory-b2`, values, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      message.success(t('Item added successfully'));
      form.resetFields();

    } catch (error) {
      console.error('Error adding inventory item:', error);
      message.error(t('Failed to add item. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    form.resetFields();
  };

  return (
    <Layout className="site-layout">
      <Sidebar />
      <Layout>
        <Header />
        <Content className="site-content">
          <div className="inventory-container">
            <Card 
              className="inventory-card"
              title={
                <Space>
                  <ShoppingOutlined />
                  <Title level={2}>{t('Inventory Management')}</Title>
                </Space>
              }
            >
              <Form
                form={form}
                layout="vertical"
                onFinish={handleAddItem}
                className="inventory-form"
                initialValues={{
                  price_per_unit: 0.0
                }}
              >
                <Form.Item
                  name="product_id"
                  label={t('Product ID')}
                  rules={[
                    { required: true, message: t('Please enter the Product ID') }
                  ]}
                >
                  <Input 
                    placeholder={t('Enter Product ID')}
                    allowClear
                  />
                </Form.Item>

                <Form.Item
                  name="item_name"
                  label={t('Item Name')}
                  rules={[
                    { required: true, message: t('Please enter the Item Name') }
                  ]}
                >
                  <Input 
                    placeholder={t('Enter Item Name')}
                    allowClear
                  />
                </Form.Item>

                <Form.Item
                  name="item_description"
                  label={t('Item Description')}
                >
                  <TextArea
                    placeholder={t('Enter Item Description')}
                    rows={4}
                    allowClear
                  />
                </Form.Item>

                <Form.Item
                  name="category"
                  label={t('Category')}
                  rules={[
                    { required: true, message: t('Please select a category') }
                  ]}
                >
                  <Select placeholder={t('Select a category')}>
                    {categories.map(category => (
                      <Option key={category.value} value={category.value}>
                        {category.label}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>

                <Form.Item
                  name="price_per_unit"
                  label={t('Price per Unit')}
                  rules={[
                    { required: true, message: t('Please enter the Price per Unit') },
                    { type: 'number', min: 0, message: t('Price must be greater than or equal to 0') }
                  ]}
                >
                  <InputNumber
                    placeholder={t('Enter Price per Unit')}
                    style={{ width: '100%' }}
                    step="0.01"
                    precision={2}
                    prefix="฿"
                  />
                </Form.Item>

                <Form.Item className="form-actions">
                  <Space size="middle">
                    <Button
                      type="primary"
                      htmlType="submit"
                      icon={<SaveOutlined />}
                      loading={loading}
                    >
                      {t('Add Item')}
                    </Button>
                    <Button 
                      onClick={handleReset}
                      icon={<ClearOutlined />}
                    >
                      {t('Reset')}
                    </Button>
                  </Space>
                </Form.Item>
              </Form>
            </Card>
          </div>
        </Content>
      </Layout>
    </Layout>
  );
};

export default withRoleAccess(InventoryPage, ['superadmin', 'admin']);